/* ============================================================
   ET - SOCIAL PROOF — Swiper slider + custom video controls.
   IIFE, no global leakage (single namespaced run-once guard).
   Inits per section instance; re-inits on shopify:section:load.
   Swiper 11 lazy-loads once from jsDelivr (shared data-et-swiper
   dedup attributes with et-materials-slider).
   ============================================================ */
(function () {
  'use strict';

  if (window.__etSocialProofInit) return;
  window.__etSocialProofInit = true;

  var SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  var SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Shared lazy Swiper loader ---------- */
  function loadSwiper(cb) {
    if (window.Swiper) { cb(); return; }
    if (!document.querySelector('link[data-et-swiper]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SWIPER_CSS;
      link.setAttribute('data-et-swiper', '');
      document.head.appendChild(link);
    }
    var existing = document.querySelector('script[data-et-swiper]');
    if (existing) {
      if (existing.dataset.loaded === '1') { cb(); }
      else { existing.addEventListener('load', cb); }
      return;
    }
    var s = document.createElement('script');
    s.src = SWIPER_JS;
    s.async = true;
    s.setAttribute('data-et-swiper', '');
    s.addEventListener('load', function () { s.dataset.loaded = '1'; cb(); });
    document.head.appendChild(s);
  }

  /* ---------- Video helpers ---------- */
  function isSpVideo(node) {
    return !!(node && node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-et-sp-video'));
  }

  function wrapOf(node) {
    return node && node.closest ? node.closest('[data-et-sp-video-wrap]') : null;
  }

  function videoOf(wrap) {
    return wrap.querySelector('[data-et-sp-video]');
  }

  function safePlay(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function syncToggle(wrap, video) {
    var toggle = wrap.querySelector('[data-et-sp-toggle]');
    if (!toggle) return;
    toggle.classList.toggle('is-paused', video.paused);
    var label = video.paused
      ? toggle.getAttribute('data-label-play')
      : toggle.getAttribute('data-label-pause');
    if (label) toggle.setAttribute('aria-label', label);
  }

  function syncMute(wrap, video) {
    var btn = wrap.querySelector('[data-et-sp-mute]');
    if (!btn) return;
    btn.classList.toggle('is-muted', video.muted);
    var label = video.muted
      ? btn.getAttribute('data-label-unmute')
      : btn.getAttribute('data-label-mute');
    if (label) btn.setAttribute('aria-label', label);
  }

  /* Pause + restore the poster / play-button overlay (currentTime kept). */
  function resetVideo(wrap) {
    var video = videoOf(wrap);
    if (!video) return;
    if (!video.paused) video.pause();
    wrap.classList.remove('is-playing');
    syncToggle(wrap, video);
  }

  function pauseAll(root, except) {
    root.querySelectorAll('[data-et-sp-video-wrap]').forEach(function (wrap) {
      if (wrap !== except) resetVideo(wrap);
    });
  }

  /* Event delegation at the section root; media events (play/pause/
     timeupdate/…) don't bubble, so they're captured instead. */
  function bindVideos(root) {
    root.addEventListener('click', function (e) {
      var wrap = wrapOf(e.target);
      if (!wrap) return;
      var video = videoOf(wrap);
      if (!video) return;

      if (e.target.closest('[data-et-sp-play]')) {
        safePlay(video);
        return;
      }

      if (e.target.closest('[data-et-sp-toggle]')) {
        if (video.paused) { safePlay(video); }
        else { video.pause(); }
        return;
      }

      if (e.target.closest('[data-et-sp-mute]')) {
        video.muted = !video.muted;
        syncMute(wrap, video);
        return;
      }

      var progress = e.target.closest('[data-et-sp-progress]');
      if (progress && video.duration) {
        var rect = progress.getBoundingClientRect();
        var ratio = (e.clientX - rect.left) / Math.max(rect.width, 1);
        ratio = Math.min(1, Math.max(0, ratio));
        video.currentTime = ratio * video.duration;
      }
    });

    root.addEventListener('play', function (e) {
      if (!isSpVideo(e.target)) return;
      var wrap = wrapOf(e.target);
      if (!wrap) return;
      pauseAll(root, wrap); /* only one video plays at a time */
      wrap.classList.add('is-playing');
      syncToggle(wrap, e.target);
      syncMute(wrap, e.target);
    }, true);

    root.addEventListener('pause', function (e) {
      if (!isSpVideo(e.target)) return;
      var wrap = wrapOf(e.target);
      if (wrap) syncToggle(wrap, e.target);
    }, true);

    root.addEventListener('ended', function (e) {
      if (!isSpVideo(e.target)) return;
      var wrap = wrapOf(e.target);
      if (wrap) resetVideo(wrap);
    }, true);

    root.addEventListener('volumechange', function (e) {
      if (!isSpVideo(e.target)) return;
      var wrap = wrapOf(e.target);
      if (wrap) syncMute(wrap, e.target);
    }, true);

    root.addEventListener('timeupdate', function (e) {
      if (!isSpVideo(e.target)) return;
      var wrap = wrapOf(e.target);
      if (!wrap) return;
      var fill = wrap.querySelector('[data-et-sp-progress-fill]');
      if (fill && e.target.duration) {
        fill.style.width = ((e.target.currentTime / e.target.duration) * 100) + '%';
      }
    }, true);
  }

  /* ---------- Swiper ---------- */
  function initSlider(root) {
    var el = root.querySelector('[data-et-sp-swiper]');
    if (!el || el.dataset.etInit === 'true') return;
    el.dataset.etInit = 'true';

    loadSwiper(function () {
      var reduced = prefersReducedMotion();
      var opts = {
        slidesPerView: 'auto',
        spaceBetween: 16,
        speed: reduced ? 0 : 450,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        a11y: Object.assign({ enabled: true }, window.etSwiperA11y),
        /* Below 750px: free scrolling, no snapping. */
        freeMode: { enabled: true, sticky: false, momentum: !reduced },
        breakpoints: {
          /* 750px and up (Swiper breakpoints are min-width): normal snap. */
          750: { freeMode: { enabled: false } }
        },
        on: {
          slideChange: function () { pauseAll(root, null); }
        }
      };

      var prevEl = root.querySelector('[data-et-sp-prev]');
      var nextEl = root.querySelector('[data-et-sp-next]');
      if (prevEl && nextEl) {
        opts.navigation = { prevEl: prevEl, nextEl: nextEl };
      }

      /* Progress bar: dark fill grows with how far you've scrolled —
         starts at the visible fraction of the track, ends at 100%. */
      var fillEl = root.querySelector('[data-et-sp-progressbar]');
      if (fillEl) {
        var updateFill = function (sw) {
          var visible = sw.virtualSize > 0 ? Math.min(1, sw.size / sw.virtualSize) : 1;
          var progress = Math.min(1, Math.max(0, sw.progress || 0));
          fillEl.style.width = ((visible + (1 - visible) * progress) * 100) + '%';
        };
        opts.on.afterInit = function (sw) { updateFill(sw); };
        opts.on.progress = function (sw) { updateFill(sw); };
        opts.on.resize = function (sw) { updateFill(sw); };
      }

      new window.Swiper(el, opts);
    });
  }

  /* ---------- Boot (per section instance) ---------- */
  function init(root) {
    if (!root || root.dataset.etSpInit === 'true') return;
    root.dataset.etSpInit = 'true';
    bindVideos(root);
    initSlider(root);
  }

  function boot() {
    document.querySelectorAll('.et-social-proof').forEach(init);
  }

  if (document.readyState !== 'loading') { boot(); }
  else { document.addEventListener('DOMContentLoaded', boot); }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.classList && e.target.classList.contains('et-social-proof')) {
      init(e.target);
      return;
    }
    if (e.target.querySelector) {
      var inner = e.target.querySelector('.et-social-proof');
      if (inner) init(inner);
    }
  });
})();
